import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { StoryGenerationJob, JobStatus, FileType } from '../types/estimation';
import { Project } from '../types';
import UploadDocumentModal from '../components/Estimation/UploadDocumentModal';
import { useNavigate } from 'react-router-dom';

const EstimationTool: React.FC = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<StoryGenerationJob[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [createManualDialogOpen, setCreateManualDialogOpen] = useState(false);
  const [manualJobName, setManualJobName] = useState('');
  const [manualJobDescription, setManualJobDescription] = useState('');
  const [manualJobProjectId, setManualJobProjectId] = useState('');
  const [creatingManual, setCreatingManual] = useState(false);

  useEffect(() => {
    loadData();
  }, [selectedProject]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [jobsData, projectsData] = await Promise.all([
        apiService.getStoryGenerationJobs(selectedProject === 'all' ? undefined : selectedProject),
        apiService.getProjects()
      ]);
      setJobs(jobsData);
      setProjects(projectsData);
    } catch (error) {
      console.error('Error loading estimation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = () => {
    setUploadModalOpen(false);
    loadData();
  };

  const handleCreateManualJob = () => {
    setManualJobName('');
    setManualJobDescription('');
    setManualJobProjectId('');
    setCreateManualDialogOpen(true);
  };

  const handleSubmitManualJob = async () => {
    if (!manualJobName.trim()) {
      alert('Job name is required');
      return;
    }

    try {
      setCreatingManual(true);
      const job = await apiService.createEstimationJob({
        name: manualJobName.trim(),
        description: manualJobDescription.trim() || undefined,
        projectId: manualJobProjectId || undefined
      });
      
      setCreateManualDialogOpen(false);
      setManualJobName('');
      setManualJobDescription('');
      setManualJobProjectId('');
      
      // Navigate to job details to add stories
      navigate(`/estimation/job/${job.id}`);
    } catch (error: any) {
      console.error('Error creating manual job:', error);
      alert(error.response?.data?.message || 'Failed to create job');
    } finally {
      setCreatingManual(false);
    }
  };

  const handleViewJob = (job: StoryGenerationJob) => {
    if (job.acceptedStories > 0) {
      // If stories are approved, go to estimation page
      navigate(`/estimation/job/${job.id}/estimation`);
    } else {
      // If no stories are approved, go to review page
      navigate(`/estimation/job/${job.id}`);
    }
  };

  const getStatusColor = (status: JobStatus) => {
    switch (status) {
      case JobStatus.COMPLETED:
        return 'success';
      case JobStatus.FAILED:
        return 'error';
      case JobStatus.PROCESSING:
        return 'warning';
      case JobStatus.PENDING:
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: JobStatus) => {
    switch (status) {
      case JobStatus.COMPLETED:
        return <CheckCircleIcon />;
      case JobStatus.FAILED:
        return <ErrorIcon />;
      case JobStatus.PROCESSING:
        return <CircularProgress size={20} />;
      case JobStatus.PENDING:
        return <ScheduleIcon />;
      default:
        return undefined;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileTypeIcon = (fileType: FileType) => {
    switch (fileType) {
      case FileType.PDF:
        return '📄';
      case FileType.DOCX:
        return '📝';
      case FileType.TXT:
        return '📃';
      case FileType.AUDIO:
        return '🎵';
      default:
        return '📎';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Estimation Tool
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleCreateManualJob}
          >
            Create Manual Job
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => setUploadModalOpen(true)}
          >
            Upload Document
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Project</InputLabel>
              <Select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                label="Filter by Project"
              >
                <MenuItem value="all">All Projects</MenuItem>
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton onClick={loadData} title="Refresh">
              <RefreshIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Paper>

      {jobs.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CloudUploadIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Story Generation Jobs
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Upload a document to start generating stories for estimation
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setUploadModalOpen(true)}
          >
            Upload Your First Document
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {jobs.map((job) => (
            <Grid item xs={12} md={6} lg={4} key={job.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>
                      {job.name}
                    </Typography>
                    <Chip
                      icon={getStatusIcon(job.status)}
                      label={job.status}
                      color={getStatusColor(job.status) as any}
                      size="small"
                    />
                  </Box>

                  {job.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {job.description}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      {getFileTypeIcon(job.fileType)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {job.fileName}
                    </Typography>
                  </Box>

                  {job.project && (
                    <Chip
                      label={job.project.name}
                      size="small"
                      variant="outlined"
                      sx={{ mb: 2 }}
                    />
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Stories: {job.totalStories}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Accepted: {job.acceptedStories}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Rejected: {job.rejectedStories}
                    </Typography>
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    Created: {formatDate(job.createdAt)}
                  </Typography>

                  {job.errorMessage && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {job.errorMessage}
                    </Alert>
                  )}
                </CardContent>

                <CardActions>
                  <Button
                    size="small"
                    startIcon={<ViewIcon />}
                    onClick={() => handleViewJob(job)}
                  >
                    {job.acceptedStories > 0 ? 'View Estimation' : 'Review Stories'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <UploadDocumentModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onSuccess={handleUploadSuccess}
        projects={projects}
      />

      {/* Create Manual Job Dialog */}
      <Dialog open={createManualDialogOpen} onClose={() => !creatingManual && setCreateManualDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Manual Estimation Job</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Job Name *"
              fullWidth
              value={manualJobName}
              onChange={(e) => setManualJobName(e.target.value)}
              margin="normal"
              required
              disabled={creatingManual}
              placeholder="e.g., E-commerce Platform - Phase 1"
            />

            <TextField
              label="Description"
              fullWidth
              value={manualJobDescription}
              onChange={(e) => setManualJobDescription(e.target.value)}
              margin="normal"
              multiline
              rows={3}
              disabled={creatingManual}
              placeholder="Optional description of what you're estimating"
            />

            <FormControl fullWidth margin="normal" disabled={creatingManual}>
              <InputLabel>Project (Optional)</InputLabel>
              <Select
                value={manualJobProjectId}
                onChange={(e) => setManualJobProjectId(e.target.value)}
                label="Project (Optional)"
              >
                <MenuItem value="">
                  <em>No Project</em>
                </MenuItem>
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                You'll be able to add stories manually after creating this job. Stories can be added one by one or you can import them later.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateManualDialogOpen(false)} disabled={creatingManual}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitManualJob}
            variant="contained"
            disabled={creatingManual || !manualJobName.trim()}
            startIcon={creatingManual ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {creatingManual ? 'Creating...' : 'Create Job'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default EstimationTool;
